import { MenuFlyout, MenuOption, MenuSection } from "@design";
import { useTranslation } from "react-i18next";
import type { ReasoningConfig, ReasoningEffort } from "@/api";
import { Text } from "@/components/Text";
import { supportedEfforts } from "./reasoning";
import type { ChatModelOption } from "./useChatModels";

interface EffortProps {
  reasoning: ReasoningConfig | null;
  effort: ReasoningEffort | null;
  onEffortChange: (effort: ReasoningEffort | null) => void;
}

/** "Effort ›" under the answering model: Default (the model's own level, sent as null), then each supported level. */
function EffortFlyout({ reasoning, effort, onEffortChange }: Readonly<EffortProps>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const efforts = supportedEfforts(reasoning);
  if (efforts.length === 0) return null;
  const defaultEffort = reasoning?.defaultEffort;
  const defaultLabel = defaultEffort
    ? t("thinkingDefaultNamed", { level: t(`effort.${defaultEffort}`) })
    : t("thinkingDefault");
  return (
    <MenuFlyout
      value={effort ? t(`effort.${effort}`) : defaultLabel}
      flyout={
        <MenuSection title={t("effortSection")}>
          <MenuOption selected={effort === null} onSelect={() => onEffortChange(null)}>
            {defaultLabel}
          </MenuOption>
          {efforts.map((e) => (
            <MenuOption key={e} selected={effort === e} onSelect={() => onEffortChange(e)}>
              {t(`effort.${e}`)}
            </MenuOption>
          ))}
        </MenuSection>
      }
    >
      {t("effort.label")}
    </MenuFlyout>
  );
}

/**
 * Response mode (tier defaults) and custom model, with the answering model's Effort right under it. Mode and model
 * can only be picked before the chat starts; effort stays editable mid-chat (as frontend/'s ReasoningEffortPicker).
 * "Choose for me" never has an Effort row: the router has no thinking level (see answeringReasoningConfig).
 */
export function TierMenu({
  options,
  selectedId,
  onSelect,
  fixedModel,
  ...effortProps
}: Readonly<
  EffortProps & {
    /** Omit once the chat has started (model is fixed). */
    options?: ChatModelOption[];
    /** The chat's model, shown read-only once it has started (or when an agent answers). */
    fixedModel?: string;
    selectedId?: string | null;
    onSelect?: (id: string) => void;
  }
>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const effortRow = <EffortFlyout {...effortProps} />;

  if (fixedModel) {
    return (
      <MenuSection title={t("modelSection")}>
        <MenuOption description={t("modelFixed")}>{fixedModel}</MenuOption>
        {effortRow}
      </MenuSection>
    );
  }

  const tiers = options?.filter((o) => o.isTier) ?? [];
  const models = options?.filter((o) => !o.isTier) ?? [];
  // The chosen custom model (or the first) stays in view; the rest sit behind "More models ›".
  const featured = models.find((m) => m.id === selectedId) ?? models[0];
  const others = models.filter((m) => m !== featured);
  const row = (o: ChatModelOption) => (
    <MenuOption key={o.id} description={o.description} selected={o.id === selectedId} onSelect={() => onSelect?.(o.id)}>
      {o.label}
    </MenuOption>
  );
  const withEffort = (o: ChatModelOption) =>
    o.id === selectedId ? [row(o), <EffortFlyout key={`${o.id}-effort`} {...effortProps} />] : row(o);

  return (
    <>
      {tiers.length > 0 && <MenuSection title={t("responseModeSection")}>{tiers.map(withEffort)}</MenuSection>}
      {featured && (
        <MenuSection title={t("customModelSection")} divider={tiers.length > 0}>
          {withEffort(featured)}
          {others.length > 0 && (
            <MenuFlyout flyout={<MenuSection title={t("moreModelsSection")}>{others.map(row)}</MenuSection>}>
              {t("moreModels")}
            </MenuFlyout>
          )}
          <Text variant="caption" tone="tertiary" className="mt-1 block border-t border-border-subtle px-3 pt-2 pb-1">
            {t("customModelNote")}
          </Text>
        </MenuSection>
      )}
    </>
  );
}
